/**
 * CarbonTexture — Procedural canvas-based carbon fibre weave.
 *
 * Generates a 2×2 diagonal twill pattern entirely in the browser — no static
 * file needed. The constructor follows the same "return-canvas" pattern as
 * Three.js FlakesTexture so it can be passed directly to CanvasTexture:
 *
 *   const map = new THREE.CanvasTexture(new CarbonTexture(256) as unknown as HTMLCanvasElement);
 *
 * Pattern design:
 * - Diagonal 2×2 twill: each row shifts the x-coordinate by 1 px, producing
 *   the characteristic 45 ° stripe of real woven carbon fibre.
 * - FIBER (8 px) controls the bundle width.  Smaller → finer weave.
 * - "Over" bundles are lighter; "under" bundles are darker — creates depth.
 * - Brightness cross-section uses sin(t·π) for a smooth rounded fibre look.
 *
 * @see docs/ims-engine-api/06-feature-color-system.md
 */

export class CarbonTexture {
  readonly width: number;
  readonly height: number;

  constructor(size = 256) {
    this.width  = size;
    this.height = size;

    // -----------------------------------------------------------------------
    // Create canvas + draw pattern
    // -----------------------------------------------------------------------

    const canvas = document.createElement('canvas');
    canvas.width  = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Allocate pixel buffer once
      const imageData = ctx.createImageData(size, size);
      const data = imageData.data;

      const FIBER  = 8;           // pixels per fibre bundle
      const PERIOD = FIBER * 2;   // 16 px = one full 2×2 twill repeat

      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          // ── Diagonal coordinate transform ─────────────────────────────
          // Subtracting y from x shifts each row left by 1 px — this is
          // what produces the 45 ° diagonal direction of the twill weave.
          const dx = ((x - y) % PERIOD + PERIOD * 64) % PERIOD;
          const dy = y % PERIOD;

          // ── 2×2 twill cell ────────────────────────────────────────────
          const cx = Math.floor(dx / FIBER); // 0 or 1
          const cy = Math.floor(dy / FIBER); // 0 or 1
          // "Over" fibre (sits on top of weave) alternates in checkerboard
          const isOver = (cx + cy) % 2 === 0;

          // ── Fibre cross-section brightness (renders the curved bundle) ─
          // fiberPos = position across the fibre width (0 → FIBER-1)
          const fiberPos = isOver ? (dy % FIBER) : (dx % FIBER);
          const t = fiberPos / (FIBER - 1); // 0 → 1
          const brightness = Math.sin(t * Math.PI); // peak at 0.5, zero at edges

          // ── Colour ────────────────────────────────────────────────────
          // "Over" bundles are lighter (they face the light source); "under"
          // bundles are darker (partially shadowed by the overlying fibre).
          const base = isOver ? 18 : 10;
          const peak = isOver ? 68 : 52;
          const v = Math.round(base + brightness * (peak - base));

          const idx = (y * size + x) * 4;
          data[idx]     = v; // R
          data[idx + 1] = v; // G
          data[idx + 2] = v; // B
          data[idx + 3] = 255;
        }
      }

      ctx.putImageData(imageData, 0, 0);
    }

    // -----------------------------------------------------------------------
    // Return the canvas so `new THREE.CanvasTexture(new CarbonTexture())`
    // works without unwrapping — identical pattern to FlakesTexture.
    // -----------------------------------------------------------------------
    return canvas as unknown as this;
  }
}
